/**
 * Crescent Flexipack — Product Detail Page.
 * Renders the Stand-Up Pouches content from the backend CMS. Shape agnostic:
 * missing sections simply don't render, so adding more products is zero-code.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, AlertTriangle, Package } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import { useCrescentT, TTSPlayer } from "./CrescentI18n";
import usePageSeo, { useBreadcrumbSchema, useFaqSchema, useServiceSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CrescentProductDetail() {
  const { slug } = useParams();
  const [raw, setRaw] = useState(null);
  const [state, setState] = useState("loading");
  const { t: product } = useCrescentT(raw);

  useEffect(() => {
    axios.get(`${API}/crescent/products/${slug}`)
      .then((r) => { setRaw(r.data.product); setState("ok"); })
      .catch(() => setState("not-found"));
  }, [slug]);

  usePageSeo(product
    ? {
        title: `${product.title} — Crescent Flexi Pack`,
        description: (product.lede || "").slice(0, 170),
      }
    : { title: "Loading…", noindex: true }
  );
  useBreadcrumbSchema(product ? [
    { name: "Home", url: "/crescent" },
    { name: "Products", url: "/crescent/products" },
    { name: product.title, url: `/crescent/products/${slug}` },
  ] : []);
  useServiceSchema(product ? { name: product.title, description: product.lede } : null);
  useFaqSchema(product?.faqs?.map((f) => ({ question: f.q, answer: f.a })) || []);

  if (state === "loading") {
    return <CrescentLayout><div className="py-24 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div></CrescentLayout>;
  }
  if (state === "not-found" || !product) {
    return (
      <CrescentLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="mt-3 font-heading text-3xl font-bold text-slate-900">Product not found</h1>
          <Link to="/crescent/products" className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-semibold">
            View all products <ArrowRight size={14} />
          </Link>
        </div>
      </CrescentLayout>
    );
  }

  return (
    <CrescentLayout>
      <div data-testid={`crescent-product-${slug}`}>
        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <nav className="text-xs text-amber-100 mb-3">
                <Link to="/crescent" className="hover:underline">Home</Link>
                <span className="mx-2">›</span>
                <Link to="/crescent/products" className="hover:underline">Products</Link>
                <span className="mx-2">›</span>
                <span className="text-white">{product.title}</span>
              </nav>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight" data-testid="product-title">{product.title}</h1>
              <p className="mt-4 text-lg text-white/90 max-w-xl">{product.lede}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/crescent/request-quote" className="bg-white text-emerald-700 hover:bg-amber-50 font-bold px-6 py-3 rounded-full inline-flex items-center gap-2">
                  Request a Quote <ArrowRight size={16} />
                </Link>
                <button onClick={() => window.dispatchEvent(new Event("open-crescent-chat"))} data-testid="product-ai-cta" className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-6 py-3 rounded-full">Chat with AI Assistant</button>
                <TTSPlayer
                  text={[product.title, product.lede, ...(product.intro || [])].filter(Boolean).join(". ")}
                  label="Listen"
                />
              </div>
            </div>
            {product.image ? (
              <img src={product.image} alt={product.title} className="w-full rounded-2xl shadow-2xl aspect-[4/3] object-cover bg-gradient-to-br from-emerald-700 to-slate-900" loading="eager"
                onError={(e)=>{e.currentTarget.style.display='none'; e.currentTarget.nextElementSibling && (e.currentTarget.nextElementSibling.style.display='block')}} />
            ) : null}
            <div className={`hidden w-full rounded-2xl shadow-2xl aspect-[4/3] bg-gradient-to-br ${product.fallback_gradient || 'from-emerald-700 to-slate-900'}`} />
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {(product.intro || []).map((p, i) => (
              <p key={i} className="text-base text-slate-700 leading-relaxed mb-4 max-w-3xl">{p}</p>
            ))}
          </div>
        </section>

        {/* Specs grid */}
        <section className="pb-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              ["Applications", product.applications],
              ["Available Formats", product.formats],
              ["Sizes & Customization", product.sizes],
              ["Printing Capabilities", product.printing],
            ].filter(([, v]) => v?.length).map(([heading, items]) => (
              <div key={heading} className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`spec-${heading.toLowerCase().replace(/\s+/g, "-")}`}>
                <h2 className="font-heading font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
                  <Package size={16} className="text-emerald-600" /> {heading}
                </h2>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Materials note */}
        {product.materials_note && (
          <section className="pb-14">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h3 className="font-bold text-amber-900 text-sm">Materials & Barrier</h3>
                <p className="mt-1 text-sm text-amber-900/90">{product.materials_note}</p>
              </div>
            </div>
          </section>
        )}

        {/* MOQ */}
        {product.moq_guidance && (
          <section className="pb-14">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-900 text-sm">MOQ Guidance</h3>
                <p className="mt-1 text-sm text-slate-700">{product.moq_guidance}</p>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {product.faqs?.length > 0 && (
          <section className="pb-14 bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-6">Frequently Asked</h2>
              <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden" data-testid="product-faqs">
                {product.faqs.map((f, i) => (
                  <details key={i} className="group" data-testid={`product-faq-${i}`}>
                    <summary className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-slate-50">
                      <span className="font-semibold text-slate-900 text-sm">{f.q}</span>
                      <span className="text-emerald-600 text-xl font-bold group-open:rotate-45 transition-transform leading-none">+</span>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related industries */}
        {product.related_industries?.length > 0 && (
          <section className="pb-14 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-2xl mb-4">Common applications</h2>
              <div className="flex flex-wrap gap-2">
                {product.related_industries.map((r) => (
                  <Link key={r} to={`/crescent/industries/${r}`} className="bg-white border border-slate-200 hover:border-emerald-500 text-sm font-semibold text-slate-700 hover:text-emerald-700 px-4 py-2 rounded-full">
                    {r.replace(/-/g, " ")}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </CrescentLayout>
  );
}
